import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BiImageAdd, BiTrash } from 'react-icons/bi'
import API from '../../api'

const DragDropFiles = ({ formData, setFormData, selectedItem, title, fileKey = "files" }) => {
    const [isDragging, setIsDragging] = useState(false)
    const fileInputRef = useRef(null)
    const { t } = useTranslation()

    const handleFileSelect = (e) => {
        const selectedFiles = Array.from(e.target.files)
        addNewFiles(selectedFiles)
    }

    const addNewFiles = (newFiles) => {
        setFormData(prevData => {
            const updatedFiles = [...(prevData[fileKey] || [])];

            newFiles.forEach(file => {
                if (file.type.match('image.*')) {
                    const fileWithPreview = {
                        file,
                        preview: URL.createObjectURL(file),
                        name: file.name,
                        size: file.size,
                        id: `${file.name}-${Date.now()}`
                    }
                    updatedFiles.push(fileWithPreview);
                }
            });

            return {
                ...prevData,
                [fileKey]: updatedFiles
            };
        });
    }

    const handleRemoveFile = (fileId, index) => {
        setFormData(prevData => {
            if (!prevData[fileKey] || !Array.isArray(prevData[fileKey])) {
                return prevData;
            }

            const fileToRemove = prevData[fileKey][index];
            if (fileToRemove && typeof fileToRemove === 'object' && fileToRemove.preview) {
                URL.revokeObjectURL(fileToRemove.preview);
            }

            return {
                ...prevData,
                [fileKey]: prevData[fileKey].filter((_, i) => i !== index)
            };
        });
    }

    const handleDragEnter = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        e.stopPropagation()
        if (!isDragging) setIsDragging(true)
    }

    const handleDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const droppedFiles = Array.from(e.dataTransfer.files)
            addNewFiles(droppedFiles)
            e.dataTransfer.clearData()
        }
    }

    const openFileSelector = () => {
        fileInputRef.current.click()
    }

    return (
        <div className="mb-4">
            <label className="form-label">{title ? title : t("images")}</label>
            <div
                className={`image-upload-area ${isDragging ? 'dragging' : ''}`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={openFileSelector}
                style={{
                    border: '2px dashed #ccc',
                    borderRadius: '6px',
                    padding: '20px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragging ? '#f8f9fa' : 'transparent',
                    minHeight: '120px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                    multiple
                    accept="image/*"
                />
                <div>
                    <BiImageAdd size={32} style={{ color: '#6c757d', marginBottom: '10px' }} />
                    <p className="mb-0">{t("drag_drop_images")}</p>
                    <p className="text-muted small">{t("or_click_to_select")}</p>
                </div>
            </div>

            {formData[fileKey]?.length > 0 && (
                <div className="d-flex flex-wrap mt-3 gap-2">
                    {formData[fileKey]?.map((file, index) => (
                        <div
                            key={typeof file === 'object' ? file?.id : `existing-file-${index}`}
                            className="position-relative"
                            style={{
                                width: '100px',
                                height: '100px',
                                overflow: 'hidden',
                                borderRadius: '4px'
                            }}
                        >
                            <img
                                src={selectedItem === 0 || typeof file === 'object' ? file?.preview : `${API.BASE}${file}`}
                                alt={typeof file === 'object' ? file?.name : `Image ${index + 1}`}
                                className="img-thumbnail"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                            <button
                                type="button"
                                className="btn btn-sm btn-danger position-absolute"
                                onClick={() => handleRemoveFile(file?.id, index)}
                                style={{
                                    top: '5px',
                                    right: '5px',
                                    padding: '2px 5px',
                                    opacity: 0.8
                                }}
                            >
                                <BiTrash size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default DragDropFiles
